/* cache-bust 20260807 */
/**
 * sr-command.js v1.2 — SR 指令系统（三层共享）
 * 
 * 架构原则：
 *   C 层：监听事件 + 调 API + dispatch 指令
 *   M 层：注册 onM(cmd, handler) → 执行数据操作
 *   V 层：注册 onV(cmd, handler) → 执行 UI 操作
 *
 * 加载顺序（theme.liquid）：
 *   1. sr-model.js        ← M 层
 *   2. sr-command.js      ← 指令系统（本文件）
 *   3. sr-controller.js   ← C 层
 *   4. sr-header-ctrl.js  ← 头部的 C 层
 *   5. sr-view-*.js       ← V 层（各页面按需加载）
 */
(function() {
'use strict';

// ═══════════════════════════════════════════
// 指令代码表
// ═══════════════════════════════════════════

var CMD = {
  // ── 邮箱 / 身份 ──
  EMAIL_REGISTER:       'email:register',       // 光板填新邮箱 → 200 新客户
  EMAIL_LOGIN:          'email:login',          // 光板填已有邮箱 → 直接登录
  EMAIL_CONFLICT:       'email:conflict',       // 邮箱冲突 → 需要 OTP
  EMAIL_CHANGE:         'email:change',         // Insider 改邮箱 → 新邮箱无人用
  EMAIL_CHANGE_CONFLICT:'email:change:conflict', // Insider 改邮箱 → 新邮箱有人用

  // ── 邮箱提交（V→C） ──
  EMAIL_SUBMIT:          'email:submit',          // V层汇报：表单提交邮箱（Center/Personal Info）

  // ── OTP ──
  OTP_SENT:             'otp:sent',             // OTP 已发送
  OTP_VERIFY_OK:        'otp:ok',               // OTP 验证通过
  OTP_VERIFY_FAIL:      'otp:fail',             // OTP 验证失败

  // ── 购物车沙盒 ──
  CART_OPEN:            'cart:open',            // 打开购物车 → 快照到 localStorage
  POPUP_OPEN:           'popup:open',           // V层汇报：打开弹窗
  CART_SAVE:            'cart:save',            // 购物车内修改数据
  CART_CLOSE:           'cart:close',           // 关闭购物车 → 合并回 sessionStorage
  CART_DISCARD:         'cart:discard',         // 放弃购物车修改

  // ── 个人信息 ──
  PROFILE_UPDATE:       'profile:update',       // 更新姓名/手机
  AVATAR_SET:           'avatar:set',           // 设置头像 URL
  AVATAR_UPLOAD_OK:     'avatar:upload:ok',     // 头像上传完成
  ADDRESS_ADD:          'address:add',          // 新增地址
  ADDRESS_UPDATE:       'address:update',       // 修改地址
  ADDRESS_DELETE:       'address:delete',       // 删除地址
  ADDRESS_DEFAULT:      'address:default',      // 设为默认地址

  // ── 个人信息操作（V→C） ──
  PROFILE_SAVE:          'profile:save',          // V层汇报：保存个人信息
  AVATAR_UPLOAD:         'avatar:upload',         // V层汇报：上传头像

  // ── 订单 ──
  ORDER_NOTE_SAVE:      'order:note:save',      // 保存订单备注

  // ── 生命周期 ──
  PAGE_EXIT:            'lifecycle:exit',       // 离开页面 → 写 cookie + KV
  PAGE_INIT:            'lifecycle:init',       // 页面初始化 → 恢复状态

  // ── 状态通知 ──
  STATE_VISITOR:        'state:visitor',
  STATE_MEMBER:         'state:member',
  STATE_ERROR:          'state:error'
};

// ═══════════════════════════════════════════
// 分发引擎
// ═══════════════════════════════════════════

var _mHandlers = {};
var _vHandlers = {};

var SRCommand = {

  // ── 指令代码表（全局引用） ──
  CMD: CMD,

  // ═════════════════════════════════════════
  // M 层注册 / 执行
  // ═════════════════════════════════════════

  /**
   * M 层注册指令处理器
   * @param {string} cmd  指令代码（用 CMD.xxx 引用）
   * @param {function} fn handler(payload) → 数据操作
   */
  onM: function(cmd, fn) {
    _mHandlers[cmd] = fn;
  },

  /**
   * 内部：执行 M 层 handler
   */
  _execM: function(cmd, payload) {
    var fn = _mHandlers[cmd];
    if (fn) return fn(payload);
    // 静默跳过未注册的 M 指令（不是每个指令都需要 M 处理）
    return null;
  },

  // ═════════════════════════════════════════
  // V 层注册 / 执行
  // ═════════════════════════════════════════

  /**
   * V 层注册指令处理器
   * @param {string} cmd  指令代码
   * @param {function} fn handler(payload) → UI 操作
   */
  onV: function(cmd, fn) {
    _vHandlers[cmd] = fn;
  },

  /**
   * 内部：执行 V 层 handler
   */
  _execV: function(cmd, payload) {
    var fn = _vHandlers[cmd];
    if (fn) return fn(payload);
    // 静默跳过未注册的 V 指令（不是每个指令都需要 V 响应）
  },

  // ═════════════════════════════════════════
  // C 层调用：统一分发
  // ═════════════════════════════════════════

  /**
   * C 层 dispatch 指令
   * 
   * 流程：
   *   1. 先通知 V 层更新 UI（同步）
   *   2. 再通知 M 层处理数据（可能返回 Promise）
   *
   * @param {string} cmd      指令代码
   * @param {object} payload  指令数据
   * @returns {*} M handler 的返回值（通常是 Promise 或 undefined）
   */
  dispatch: function(cmd, payload) {
    if (typeof console !== 'undefined' && console.log) {
      console.log('[SR_CMD] →', cmd, payload ? Object.keys(payload) : '(no payload)');
    }
    // 1. 通知 V（同步或返回 Promise）
    var vResult = SRCommand._execV(cmd, payload);
    // 2. 通知 M（可能异步，返回 Promise）
    var mResult = SRCommand._execM(cmd, payload);
    // 优先返回 V handler 结果（C层注册的 onV 业务处理器）
    return vResult !== undefined ? vResult : mResult;
  }
};

// 挂载到全局
window.SRCommand = SRCommand;

})();

